import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { classNames } from "primereact/utils";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import DividerWithText from "../components/dividerWithText";
import msicon from "../assets/ms-icon.png";
import { useNavigate } from "react-router-dom";
import useLogin from "../hooks/useLogin";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";

type Inputs = {
  email: string;
  password: string;
};

const MainLayoutContainer = styled.div`
  min-height: 100vh;
  min-width: 100vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: var(--sumi-sidenav-background);

  a {
    color: var(--sumi-text-primary);
    text-decoration: underline;
    cursor: pointer;
  }

  h1 {
    font-size: 36px;
    color: var(--text-color);
  }

  & label {
    font-size: 12px;
  }
`;

const Form = styled.div<{ $dark: boolean }>`
  width: 90%;
  max-width: 500px;
  padding: 3rem;
  background-color: ${(props) =>
    props.$dark ? "var(--sumi-sidenav-background)" : "var(--blue-200)"};
  color: var(--sumi-text-primary);
  border-radius: 5px;
`;

const InputContainer = styled.div`
  width: 100%;
  height: 105px;
  text-align: left;

  & .p-inputtext {
    height: 56px;
  }

  & div,
  .p-float-label,
  .p-inputtext,
  .p-password,
  .p-button {
    width: 100%;
  }

  & .forgot-password-link {
    color: var(--sumi-text-primary);
    cursor: pointer;
    text-decoration: underline;
  }
`;

const CreateAccountContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
  text-align: center;
  color: var(--text-color);

  & a {
    margin-left: 0.5rem;
    cursor: pointer;
  }
`;

export const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [theme] = useUIStore(useShallow((state: UIState) => [state.theme]));

  const { login, isLoggingIn } = useLogin();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: formData,
  });

  const handleLogin = (data: Inputs) => {
    login(data.email, data.password);
  };

  const getFormErrorMessage = (name: string) => {
    return errors[name] ? (
      <small className="p-error">{errors[name].message}</small>
    ) : (
      <></>
    );
  };

  const handleLoginRedirect = () => {
    window.location = import.meta.env.VITE_REDIRECT_URI;
  };

  return (
    <MainLayoutContainer className="login-screen">
      <img
        src={theme === "dark" ? "/sumion-white.svg" : "/sumion-blue.svg"}
        alt="Sumi Logo"
        style={{ marginBottom: "1rem", width: "156px" }}
      />
      <Form $dark={theme === "dark"}>
        <form onSubmit={handleSubmit(handleLogin)}>
          <div>
            <Controller
              name="email"
              control={control}
              rules={{
                required: t("warning-required"),
                pattern: {
                  value:
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  message: t("warning-invalid-email"),
                },
              }}
              render={({ field, fieldState }) => (
                <InputContainer>
                  <label htmlFor={field.name}>{t("label-email")}</label>
                  <InputText
                    id={field.name}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setFormData({ ...formData, email: e.target.value });
                    }}
                    className={classNames({ "p-invalid": fieldState.error })}
                    placeholder={t("label-email")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSubmit(handleLogin)();
                      }
                    }}
                  />
                  {getFormErrorMessage(field.name)}
                </InputContainer>
              )}
            />
            <Controller
              name="password"
              control={control}
              rules={{ required: t("warning-required") }}
              render={({ field, fieldState }) => (
                <InputContainer>
                  <label htmlFor={field.name}>{t("label-password")}</label>
                  <Password
                    id={field.name}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setFormData({ ...formData, password: e.target.value });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSubmit(handleLogin)();
                      }
                    }}
                    className={classNames({ "p-invalid": fieldState.error })}
                    feedback={false}
                    toggleMask
                    pt={{
                      showIcon: { style: { verticalAlign: "unset" } },
                      hideIcon: { style: { verticalAlign: "unset" } },
                    }}
                    placeholder={t("label-password")}
                  />
                  {getFormErrorMessage(field.name)}
                </InputContainer>
              )}
            />
            <InputContainer style={{ height: "25px" }}>
              <a
                role="button"
                tabIndex={0}
                id="forgot-password-link"
                onClick={() => navigate("/restore-password")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    navigate("/restore-password");
                  }
                }}
              >
                {t("label-forgot-password")}
              </a>
            </InputContainer>
            <InputContainer style={{ height: "75px" }}>
              <Button
                id="login-button"
                type="submit"
                label={t("cmd-login")}
                loading={isLoggingIn}
                style={{ height: "50px", marginTop: "1rem" }}
              />
            </InputContainer>
            <DividerWithText text={t("label-or-login-using")} />
            <InputContainer>
              <Button
                id="redirect-button"
                type="button"
                label={t("label-microsoft-login")}
                onClick={handleLoginRedirect}
                icon={<img src={msicon} alt="ms-icon" />}
                iconPos="left"
                style={{
                  backgroundColor: "#fff",
                  color: "#333333",
                  height: "56px",
                  border: "none",
                }}
              />
            </InputContainer>
          </div>
        </form>
      </Form>

      <CreateAccountContainer>
        <span style={{ color: "var(--sumi-text-primary)" }}>
          {t("label-no-account")}
        </span>
        <a
          style={{ color: "var(--sumi-text-primary)" }}
          target="_blank"
          href="https://sumi.fi/liity-tuottajayhteis%C3%B6%C3%B6n/"
          rel="noreferrer"
        >
          {t("label-create-account")}
        </a>
      </CreateAccountContainer>
    </MainLayoutContainer>
  );
};
